import './styles.sass';

import { debounce, filter, forEach, last, map, merge, sum, template } from 'lodash-es';

import React, { useEffect, useMemo, useRef } from 'react';
import useQueryParams from 'hooks/useQueryParams';
import useNavigateParams from 'hooks/useNavigateParams';
import PropTypes from 'prop-types';
import Highcharts from 'util/highcharts';
import HighchartsReact from 'highcharts-react-official';
import dayjs from 'util/date';
import NumberHelper from 'util/number_helper';

import config from 'util/site_config';
import downloadImageCredit from 'components/Explorer/util/downloadImageCredit';
import downloadSubtitle from 'components/Explorer/util/downloadSubtitle';
import * as QueryParams from 'components/Explorer/models/query_params';

const ANALYTICS_DEBOUNCE_TIME = 1000;

// Match sources.yml ordering. Video excluded (exclusionary_flag in sources.yml).
const SERIES_COLORS = [
  '#B60000',
  '#e89500',
  '#270a63',
  '#d45029',
  '#2F90B9',
  '#bdbdbd',
  '#df931b',
  '#071D70',
  '#3b2a3d',
  '#912470',
  '#00BFFF',
  '#B9DDEB',
  '#CC3300',
  '#CB2D2D',
  '#EFEFEF',
  '#15BABC',
  '#1185FE',
  '#90089C',
  '#f24c5b'
];

const CHART_MIN_RANGE = 60 * 60 * 24 * 7 * 1000;
const CHART_MIN_TICK = 60 * 60 * 24 * 1000;

// Our grouping units have nothing between a month and a year, so a 30-year view on a wide screen falls just short of
// Highcharts' switch to years and draws ~360 hairline monthly bars. Any view spanning 25 years or more gets one bar per
// year instead.
const YEARLY_GROUPING_MIN_SPAN = 60 * 60 * 24 * 365 * 25 * 1000;
const GROUPING_UNITS = [
  ['day', [1]],
  ['week', [1]],
  ['month', [1]],
  ['year', [1]]
];
const YEARLY_GROUPING_UNITS = [['year', [1]]];

const tooltipTemplate = template(
  `<table>
    <tr class="header">
      <td colspan="3"><%= I18n.t("Explorer.Timeline.chart.tooltip_title") %> <%= date %></td>
    </tr>
    <% forEach(types, function(type) { %>
      <tr>
        <td class="count"><%= Highcharts.numberFormat(type.y, 0) %></td>
        <td class="symbol" style="color:<%= type.series.color %>">●</td>
        <td class="label">
          <%= I18n.t(type.series.userOptions.id, {scope: "Explorer.Timeline.chart.types", count: total}) %>
          <%= I18n.t("Explorer.Timeline.chart.legend.mentions", { count: total }) %>
        </td>
      </tr>
    <% }); %>
    <% if (types.length > 1) { %>
      <tr class="footer">
        <td class="count"><%= Highcharts.numberFormat(total, 0) %></td>
        <td colspan="2" class="label"><%= I18n.t("Explorer.Timeline.chart.total", {count: total}) %></td>
      </tr>
    <% } %>
  </table>`,
  { imports: { Highcharts, forEach } }
);

const timeframeFor = (params, latestChartDate) => {
  const tf = params.filters.timeframe;
  if (!tf || tf === 'at') return undefined;
  const units = { d: 'day', w: 'week', m: 'month', y: 'year' };
  const [timespan, unit] = tf;
  const startTime = dayjs().startOf('day').subtract(parseInt(timespan), units[unit]);
  const endTime = latestChartDate.startOf('day');
  return { min: startTime.valueOf(), max: endTime.valueOf() };
};

const dataForChartFrom = (data) =>
  map(config.postTypes, (type, index) => ({
    id: type,
    name: I18n.t(type, { scope: 'Explorer.Timeline.chart.types' }),
    data: map(data, (row) => [row[0], row[index + 1]])
  }));

const dataForNavigationFrom = (data) =>
  map(data, (row) => {
    const [timestamp, ...rowData] = row;
    return [timestamp, sum(rowData)];
  });

const Chart = ({ data, selectedPostTypes, latestChartDate, totalOutputs, queryDescription }) => {
  const params = useQueryParams();
  const navigateParams = useNavigateParams();
  // Read the chart from HighchartsReact's ref rather than its callback prop: exporting builds a temporary copy of
  // the chart with the same callback and then destroys it, which would leave a callback-held ref on the dead copy.
  const chartComponentRef = useRef(null);

  // Read inside showTimePointMentions via refs rather than direct closures, since the
  // click handler is bound once at chart creation (see allowChartUpdate below) and
  // must still see whatever the user currently has selected.
  const selectedPostTypesRef = useRef(selectedPostTypes);
  selectedPostTypesRef.current = selectedPostTypes;
  const paramsRef = useRef(params);
  paramsRef.current = params;

  const debouncedTrackZoom = useMemo(
    () =>
      debounce(
        (e) => Analytics.trackEvent('timeline-chart-zoom', { trigger: e.trigger, min: e.min, max: e.max }),
        ANALYTICS_DEBOUNCE_TIME
      ),
    []
  );

  const showTimePointMentions = (e, context) => {
    const startDate = dayjs.utc(e.point.x);
    let endDate = startDate;
    if (context.currentDataGrouping) {
      endDate = dayjs
        .utc(e.point.x)
        .add(context.currentDataGrouping.count, context.currentDataGrouping.unitName)
        .subtract(1, 'day');
    }
    const filters = {
      mentioned_after: startDate.format('YYYY-MM-DD'),
      mentioned_before: endDate.format('YYYY-MM-DD')
    };
    if (selectedPostTypesRef.current.length > 0) {
      filters['mention_sources[]'] = selectedPostTypesRef.current.map((type) => `type:${type}`);
    }
    Analytics.trackEvent('timeline-click-bar', filters);
    navigateParams(QueryParams.withFilters(paramsRef.current, merge({ ...paramsRef.current.filters }, filters)), {
      pathname: '/mentions'
    });
  };

  // HighchartsReact auto-updates on every render, and Highcharts destroys/recreates
  // UI elements (range selector, navigator) whenever it sees a "changed" array or a
  // hardcoded rangeSelector.selected - so we opt out (allowChartUpdate={false}) and
  // build this once, at creation (SOI-1180, see highcharts/highcharts-react#328).
  // data/timeframe changes always remount Chart (Timeline/index.jsx unmounts it while
  // reloading), so only selectedPostTypes needs an update effect - see below.
  const initialChartConfig = useMemo(() => {
    const timeframe = timeframeFor(params, latestChartDate);
    const summaryDescription = I18n.t('Explorer.Timeline.Summary.research_outputs', {
      count: totalOutputs,
      formattedCount: NumberHelper.formatNumberWithDelimiter(totalOutputs)
    });
    const filename = `Altmetric Explorer Timeline - ${downloadSubtitle(queryDescription, false)}`;
    // Open on the last 30 years rather than all time: a handful of mentions dated to the early 1900s would otherwise
    // squash recent attention into a sliver. Highcharts disables this button and selects "All time" instead when the
    // data spans less than 30 x 365 days.
    const thirtyYears = { type: 'year', count: 30, text: I18n.t('Explorer.Timeline.chart.zoom.thirty_years') };
    const zoomButtons = [
      { type: 'week', count: 1, text: I18n.t('Explorer.Timeline.chart.zoom.week') },
      { type: 'month', count: 1, text: I18n.t('Explorer.Timeline.chart.zoom.one_month') },
      { type: 'month', count: 3, text: I18n.t('Explorer.Timeline.chart.zoom.three_months') },
      { type: 'month', count: 6, text: I18n.t('Explorer.Timeline.chart.zoom.six_months') },
      { type: 'year', count: 1, text: I18n.t('Explorer.Timeline.chart.zoom.one_year') },
      thirtyYears,
      { type: 'all', text: I18n.t('Explorer.Timeline.chart.zoom.all_time') }
    ];
    // The default 30-year view spans 25 years whenever the data does. After that, only regroup when a zoom crosses the
    // 25-year mark, since setDataGrouping rebuilds every series.
    let groupedByYear =
      (timeframe ? timeframe.max - timeframe.min : last(data)[0] - data[0][0]) >= YEARLY_GROUPING_MIN_SPAN;
    const regroupFor = (e) => {
      const axis = e.target;
      const byYear = (e.max ?? axis.dataMax) - (e.min ?? axis.dataMin) >= YEARLY_GROUPING_MIN_SPAN;
      if (byYear === groupedByYear) return;
      groupedByYear = byYear;
      axis.setDataGrouping({ forced: true, units: byYear ? YEARLY_GROUPING_UNITS : GROUPING_UNITS }, false);
    };

    return {
      chart: { height: 550, type: 'column', style: { fontFamily: 'Inter, Helvetica, Arial, sans-serif' } },
      colors: SERIES_COLORS,
      credits: { text: downloadImageCredit() },
      exporting: {
        enabled: true,
        buttons: { contextButton: { menuItems: ['downloadPNG'] } },
        filename,
        chartOptions: {
          legend: { enabled: true },
          navigator: { enabled: false },
          rangeSelector: { enabled: false },
          scrollbar: { enabled: false },
          title: { text: 'Altmetric Explorer Timeline' },
          subtitle: { text: downloadSubtitle(queryDescription, true) },
          caption: { text: summaryDescription, style: { fontSize: 12 } },
          chart: { spacing: [26, 26, 31, 26] },
          yAxis: { title: { text: 'Mentions' } }
        }
      },
      tooltip: {
        shadow: false,
        backgroundColor: 'rgba(245, 245, 255, 0.95)',
        headerFormat: '{point.key}',
        style: { lineHeight: '17px', padding: '13px' },
        borderWidth: 1,
        borderRadius: 3,
        borderColor: 'rgba(235, 235, 245, 1)',
        useHTML: true,
        shared: true,
        formatter: function (tooltip) {
          const item = this.points[0];
          const date = tooltip.headerFooterFormatter(item);
          const total = item.total;
          return tooltipTemplate({ date, types: filter(this.points, (p) => p.y), total });
        }
      },
      plotOptions: {
        column: {
          stacking: 'normal',
          pointPadding: 0,
          groupPadding: 0.05,
          cursor: 'pointer',
          dataGrouping: {
            approximation: 'sum',
            groupPixelWidth: 30,
            forced: true,
            units: groupedByYear ? YEARLY_GROUPING_UNITS : GROUPING_UNITS
          },
          events: {
            click: function (e) {
              return showTimePointMentions(e, this);
            }
          }
        }
      },
      xAxis: {
        ordinal: false,
        minRange: CHART_MIN_RANGE,
        minTickInterval: CHART_MIN_TICK,
        events: {
          setExtremes: (e) => {
            debouncedTrackZoom(e);
            regroupFor(e);
          }
        },
        labels: { style: { color: '#333333', cursor: 'default', fontSize: '11px' } },
        ...(timeframe || {})
      },
      yAxis: {
        minTickInterval: 1,
        allowDecimals: false,
        offset: 40,
        labels: { style: { color: '#333333', cursor: 'default', fontSize: '11px' } }
      },
      rangeSelector: {
        inputEnabled: false,
        buttonSpacing: 5,
        buttonTheme: {
          width: 100,
          r: 3,
          fill: '#E5E7F2',
          states: {
            hover: { fill: '#F2F4FA' },
            select: { fill: '#1565c0', style: { color: '#FFFFFF' } },
            disabled: { style: { cursor: 'not-allowed' } }
          }
        },
        buttons: zoomButtons,
        selected: timeframe ? null : zoomButtons.indexOf(thirtyYears)
      },
      navigator: {
        maskFill: 'rgba(28, 164, 244, 0.3)',
        series: {
          lineColor: '#1CA4F4',
          fillOpacity: 0,
          dataGrouping: { approximation: 'sum' },
          data: dataForNavigationFrom(data)
        }
      },
      series: dataForChartFrom(data)
    };
  }, []);

  useEffect(() => {
    const chart = chartComponentRef.current?.chart;
    if (!chart) return;
    chart.series.forEach((s) => {
      const selected =
        selectedPostTypes.length === 0 || s.name === 'Navigator' || selectedPostTypes.indexOf(s.userOptions.id) > -1;
      s.setVisible(selected, false);
    });
    chart.redraw();
  }, [selectedPostTypes]);

  return (
    <HighchartsReact
      ref={chartComponentRef}
      highcharts={Highcharts}
      options={initialChartConfig}
      constructorType={'stockChart'}
      allowChartUpdate={false}
      containerProps={{ 'data-testid': 'chart' }}
    />
  );
};

Chart.propTypes = {
  data: PropTypes.array.isRequired,
  selectedPostTypes: PropTypes.array.isRequired,
  latestChartDate: PropTypes.object.isRequired,
  totalOutputs: PropTypes.number.isRequired,
  queryDescription: PropTypes.string.isRequired
};

export default Chart;
